import { useRef } from "react";
import { useTranslation } from "../../i18n";
import PhoneStage, { PARTIAL_SLIDES } from "../../components/phone-stage";
import AppMapScreen from "./assets/app-map-screen.jpeg";
import AppStoreBadge from "./assets/app-store-badge.webp";
import GooglePlayBadge from "./assets/google-play-badge.webp";

// Partial-whitelabel slider frames (JUHUU app, branded per operator)
import wlStandaloneMap from "./assets/wl-standalone-map.jpeg";

const PAY_METHODS = [
  "visa",
  "mastercard",
  "maestro",
  "american-express",
  "apple-pay",
  "google-pay",
  "paypal",
  "klarna",
  "discover",
  "diners",
  "jcb",
  "unionpay",
];

const PARTNER_LOGOS = [
  ["oebb", "ÖBB"],
  ["fellbachgo", "Fellbach.go!"],
  ["koch-partner", "Koch & Partner"],
  ["allgaeutissimo", "Allgäutissimo Bikeshop"],
  ["velodepo", "VELODEPO"],
  ["opg", "OPG"],
  ["greenguard", "Greenguard"],
  ["emz", "emz"],
  ["hochschule-ansbach", "Hochschule Ansbach"],
  ["gablitz", "Gablitz"],
  ["mauerbach", "Mauerbach"],
  ["purkersdorf", "Stadtgemeinde Purkersdorf"],
  ["w5ww", "Wir 5 im Wienerwald"],
];


const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    className="h-4 w-4 flex-shrink-0 text-juhuu"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

export default function NewApp() {
  const { t } = useTranslation();
  const marqueeRef = useRef<HTMLDivElement>(null);

  const bullets = (base: string) =>
    [0, 1, 2, 3].map((i) => t(`${base}.${i}`));

  return (
    <div className="min-h-screen overflow-x-hidden relative bg-white">
      <main className="relative z-10">
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="relative overflow-hidden px-4 md:px-8 pt-32 pb-20">
          <div className="pointer-events-none absolute -top-36 -right-24 h-[480px] w-[480px] rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 md:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-3.5 py-1.5 text-sm font-bold text-juhuu-deep">
                <span className="h-[7px] w-[7px] rounded-full bg-juhuu" />
                {t("routes.app.heroBadge")}
              </div>

              <h1 className="mt-5 text-[clamp(2.3rem,4.4vw,3.9rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
                {t("routes.app.heroTitle")}
                <br />
                <span className="text-juhuu">
                  {t("routes.app.heroHighlight")}
                </span>
              </h1>

              <p className="mt-5 max-w-[520px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
                {t("routes.app.heroDescription")}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/meeting"
                  className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
                >
                  {t("routes.app.ctaBookDemo")}
                </a>
                <a
                  href="#deploy"
                  className="inline-flex items-center rounded-full border border-line-strong px-7 py-3.5 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
                >
                  {t("routes.app.ctaSeeOptions")}
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <a
                  href="https://apps.apple.com/at/app/juhuu/id1603108125"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex transition-transform hover:-translate-y-0.5"
                >
                  <img
                    src={AppStoreBadge}
                    alt={t("routes.app.appStoreAlt")}
                    className="block h-12 w-auto"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.juhuu_bikebox.juhuu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex transition-transform hover:-translate-y-0.5"
                >
                  <img
                    src={GooglePlayBadge}
                    alt={t("routes.app.googlePlayAlt")}
                    className="block h-12 w-auto"
                  />
                </a>
              </div>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[.86rem] font-semibold text-muted">
                <span>{t("routes.app.platformIosAndroid")}</span>
                <span>{t("routes.app.platformWebApp")}</span>
                <span>{t("routes.app.platformWhitelabel")}</span>
                <span>{t("routes.app.platform14Languages")}</span>
              </div>
            </div>

            <div className="relative flex justify-center">
              <div className="pointer-events-none absolute top-8 left-5 h-80 w-80 rounded-full bg-juhuu-glow blur-[90px]" />
              <div className="relative z-10 w-[300px] max-w-full animate-floaty rounded-[42px] bg-ink p-2.5 shadow-2xl">
                <div className="relative overflow-hidden rounded-[33px] bg-white">
                  <img
                    src={AppMapScreen}
                    alt={t("routes.app.heroPhoneAlt")}
                    className="block h-auto w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Three ways into the app ──────────────────────── */}
        <section
          id="deploy"
          className="scroll-mt-20 bg-bg-alt px-4 md:px-8 py-20"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-[640px]">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.app.page.deployKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.deployTitle")}
              </h2>
            </div>

            <div className="overflow-hidden rounded-2xl border border-line bg-line">
              <div className="grid gap-px md:grid-cols-3">
                {[0, 1, 2].map((i) => {
                  const featured = i === 1;
                  return (
                    <div
                      key={i}
                      className={`p-8 ${
                        featured
                          ? "bg-gradient-to-b from-juhuu-soft to-white"
                          : "bg-white"
                      }`}
                    >
                      <div
                        className={`text-[.68rem] font-bold uppercase tracking-[.1em] ${
                          featured ? "text-juhuu" : "text-muted"
                        }`}
                      >
                        {t(`routes.app.page.deployCells.${i}.tag`)}
                      </div>
                      <div
                        className={`my-4 grid h-[38px] w-[38px] place-items-center rounded-xl border ${
                          featured
                            ? "border-juhuu bg-juhuu text-white"
                            : "border-line bg-bg-alt text-ink-2"
                        }`}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          className="h-5 w-5"
                        >
                          {i === 0 && (
                            <>
                              <rect x="7" y="2" width="10" height="20" rx="2" />
                              <path d="M11 18h2" />
                            </>
                          )}
                          {i === 1 && (
                            <>
                              <rect x="5" y="2" width="14" height="20" rx="3" />
                              <circle cx="12" cy="18" r="1" />
                              <path d="M9 6h6" />
                            </>
                          )}
                          {i === 2 && (
                            <>
                              <circle cx="12" cy="12" r="9" />
                              <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                            </>
                          )}
                        </svg>
                      </div>
                      <h3 className="text-[1.15rem] font-bold text-ink">
                        {t(`routes.app.page.deployCells.${i}.title`)}
                      </h3>
                      <p className="mt-2 text-[.9rem] leading-[1.6] text-muted">
                        {t(`routes.app.page.deployCells.${i}.description`)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ── Partial whitelabel ───────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.app.page.partialKicker")}
              </p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.partialTitleLine1")}
                <br />
                {t("routes.app.page.partialTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.app.page.partialDescription")}
              </p>
              <div className="flex flex-col gap-3">
                {bullets("routes.app.page.partialBullets").map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 text-[.95rem] font-medium text-ink-2"
                  >
                    <CheckIcon />
                    {b}
                  </div>
                ))}
              </div>
            </div>
            <PhoneStage slides={PARTIAL_SLIDES} />
          </div>
        </section>

        {/* ── Standalone whitelabel ────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 md:grid-cols-2">
            <div className="order-2 md:order-1">
              <PhoneStage
                single
                slides={[{ src: wlStandaloneMap, label: "" }]}
              />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.app.page.standaloneKicker")}
              </p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.standaloneTitleLine1")}
                <br />
                {t("routes.app.page.standaloneTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.app.page.standaloneDescription")}
              </p>
              <div className="flex flex-col gap-3">
                {bullets("routes.app.page.standaloneBullets").map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 text-[.95rem] font-medium text-ink-2"
                  >
                    <CheckIcon />
                    {b}
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="/meeting"
                  className="inline-flex items-center rounded-full bg-juhuu px-6 py-3 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
                >
                  {t("routes.app.ctaBookDemo")}
                </a>
                <a
                  href="/references"
                  className="inline-flex items-center rounded-full border border-line-strong px-6 py-3 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
                >
                  {t("routes.app.page.standaloneCtaReferences")}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Everything a booking needs ───────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-12 max-w-[620px] text-center">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.app.page.featuresKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.featuresTitle")}
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-line bg-white p-7 shadow-sm"
                >
                  <div className="mb-4 grid h-12 w-12 place-items-center rounded-xl border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-6 w-6"
                    >
                      {i === 0 && <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />}
                      {i === 1 && (
                        <>
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <path d="M2 10h20" />
                        </>
                      )}
                      {i === 2 && (
                        <>
                          <rect x="3" y="4" width="18" height="16" rx="2" />
                          <circle cx="9" cy="10" r="2" />
                          <path d="M5 18a4 4 0 0 1 8 0M15 9h3M15 13h3" />
                        </>
                      )}
                    </svg>
                  </div>
                  <h3 className="text-[1.12rem] font-bold text-ink">
                    {t(`routes.app.page.featuresList.${i}.title`)}
                  </h3>
                  <p className="mt-2 text-[.9rem] leading-[1.6] text-muted">
                    {t(`routes.app.page.featuresList.${i}.description`)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-line bg-white p-8 shadow-sm">
              <p className="mb-4 text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                {t("routes.app.page.paymentsKicker")}
              </p>
              <div className="flex flex-wrap gap-2">
                {PAY_METHODS.map((p) => (
                  <img
                    key={p}
                    src={require(`./assets/pay/${p}.svg`)}
                    alt={p}
                    loading="lazy"
                    className="block h-[34px] w-auto rounded-lg bg-white shadow-sm"
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Hardware + use cases ─────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-[720px]">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.app.page.hardwareKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.hardwareTitleLine1")}
                <br />
                {t("routes.app.page.hardwareTitleLine2")}
              </h2>
              <p className="mt-4 text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
                {t("routes.app.page.hardwareLead")}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => {
                const featured = i === 2;
                return (
                  <div
                    key={i}
                    className={`rounded-2xl border p-8 shadow-sm ${
                      featured
                        ? "border-juhuu-soft-2 bg-gradient-to-br from-juhuu-soft to-white"
                        : "border-line bg-white"
                    }`}
                  >
                    <div
                      className={`mb-4 grid h-12 w-12 place-items-center rounded-xl border ${
                        featured
                          ? "border-juhuu bg-juhuu text-white"
                          : "border-juhuu-soft-2 bg-juhuu-soft text-juhuu"
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-6 w-6"
                      >
                        {i === 0 && (
                          <>
                            <rect x="5" y="11" width="14" height="10" rx="2" />
                            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                          </>
                        )}
                        {i === 1 && (
                          <>
                            <rect x="4" y="4" width="16" height="16" rx="2" />
                            <rect x="9" y="9" width="6" height="6" />
                            <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
                          </>
                        )}
                        {i === 2 && (
                          <>
                            <rect x="2" y="7" width="20" height="10" rx="2" />
                            <path d="M6 11v2M10 11v2M14 11v2M18 11v2" />
                          </>
                        )}
                      </svg>
                    </div>
                    <h3 className="text-[1.15rem] font-bold text-ink">
                      {t(`routes.app.page.hardwareCards.${i}.title`)}
                    </h3>
                    <p className="mt-2.5 text-[.92rem] leading-[1.6] text-muted">
                      {t(`routes.app.page.hardwareCards.${i}.description`)}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mb-6 mt-14 max-w-[680px]">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                {t("routes.app.page.useCasesKicker")}
              </p>
              <h2 className="mt-3 text-[clamp(1.5rem,2.3vw,2.1rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.app.page.useCasesTitle")}
              </h2>
              <p className="mt-3 text-base leading-[1.65] text-muted">
                {t("routes.app.page.useCasesDescription")}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  href: "/industries#parking",
                  label: t("routes.app.page.useCaseBikeParking"),
                  icon: (
                    <>
                      <rect x="3" y="7" width="18" height="13" rx="2" />
                      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </>
                  ),
                },
                {
                  href: "/industries#rental",
                  label: t("routes.app.page.useCaseBikeRental"),
                  icon: (
                    <>
                      <circle cx="6" cy="17" r="3" />
                      <circle cx="18" cy="17" r="3" />
                      <path d="M6 17 10 9h4m1 8-4-8m0 0 2-3h3" />
                    </>
                  ),
                },
                {
                  href: "/industries",
                  label: t("routes.app.page.useCaseSharedMobility"),
                  icon: (
                    <>
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="18" r="3" />
                      <path d="M6 18 9 6h6l3 12" />
                    </>
                  ),
                },
              ].map((u, i) => (
                <a
                  key={i}
                  href={u.href}
                  className="block rounded-2xl border border-line bg-white p-6 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-6 w-6"
                    >
                      {u.icon}
                    </svg>
                  </div>
                  <div className="text-[.95rem] font-bold text-ink">
                    {u.label}
                  </div>
                  <div className="mt-1 text-[.72rem] text-muted">
                    {t("routes.app.page.useCaseStandard")}
                  </div>
                </a>
              ))}

              <a
                href="/industries#custom"
                className="block rounded-2xl border border-juhuu-soft-2 bg-juhuu-soft p-6 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-white text-juhuu">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-6 w-6"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </div>
                <div className="text-[.95rem] font-bold text-juhuu-deep">
                  {t("routes.app.page.useCaseOwnTitle")}
                </div>
                <div className="mt-1 text-[.72rem] text-juhuu">
                  {t("routes.app.page.useCaseOwnSubtitle")}
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* ── Trusted by (logo marquee) ────────────────────── */}
        <section className="px-4 md:px-8 py-14">
          <div className="mx-auto max-w-7xl">
            <p className="mb-6 text-center text-[.82rem] font-bold uppercase tracking-[.14em] text-muted">
              {t("routes.app.page.logosTitle")}
            </p>
            <div className="w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
              <div
                ref={marqueeRef}
                className="flex w-max animate-marquee items-center gap-[clamp(2rem,5vw,4rem)] hover:[animation-play-state:paused]"
              >
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map(([slug, name], i) => (
                  <span key={i} className="inline-flex flex-shrink-0">
                    <img
                      src={require(`./assets/logos/${slug}.png`)}
                      alt={name}
                      loading="lazy"
                      className="h-10 w-auto max-w-[170px] object-contain opacity-55 transition-opacity hover:opacity-100"
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[640px]">
            <h2 className="text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
              {t("routes.app.page.finalCtaTitle")}
            </h2>
            <p className="mx-auto my-6 max-w-[480px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.app.page.finalCtaDescription")}
            </p>
            <a
              href="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.app.ctaBookDemo")}
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
